import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * HeroSlideshow — cross-fades through a list of full-bleed cinematic images.
 * Each active slide gets a slow Ken Burns zoom so the hero never feels static.
 * Re-uses the page's existing gradient stack (kept outside this component) so
 * tone stays consistent across photos.
 */
const HeroSlideshow = ({ slides = [], intervalMs = 6500, fadeMs = 1600 }) => {
  const [index, setIndex] = useState(0);
  const timer = useRef(null);

  // Preload upcoming images so the fade never reveals a blank frame
  useEffect(() => {
    slides.forEach((s) => { const img = new Image(); img.src = s.src; });
  }, [slides]);

  useEffect(() => {
    if (slides.length <= 1) return;
    timer.current = setTimeout(
      () => setIndex((i) => (i + 1) % slides.length),
      intervalMs,
    );
    return () => clearTimeout(timer.current);
  }, [index, slides.length, intervalMs]);

  if (!slides.length) return null;

  // Per-slide pan controls — VERY subtle. Defaults to a slow outward breath.
  const getScales = (pan) => {
    switch (pan) {
      case 'in':   return { from: 1.00, to: 1.05 };   // gently zoom into detail
      case 'none': return { from: 1.00, to: 1.00 };   // hold the full image still
      case 'out':
      default:     return { from: 1.05, to: 1.00 };   // start slightly tight, ease outward to reveal full image
    }
  };

  const active = slides[index];
  const { from: scaleFrom, to: scaleTo } = getScales(active.pan);

  return (
    <div className="absolute inset-0">
      <AnimatePresence mode="sync">
        <motion.div
          key={index}
          className="absolute inset-0"
          // Per-slide subtle pan. Direction is controlled per slide via `pan` prop.
          initial={{ opacity: 0, scale: scaleFrom }}
          animate={{ opacity: 1, scale: scaleTo }}
          exit={{ opacity: 0 }}
          transition={{
            opacity: { duration: fadeMs / 1000, ease: 'easeInOut' },
            // Scale spans the full slide duration, with a smooth linear feel so
            // the motion is barely noticeable but still alive.
            scale:   { duration: (intervalMs + fadeMs) / 1000, ease: 'linear' },
          }}
        >
          <img
            src={slides[index].src}
            alt={slides[index].alt || ''}
            className="w-full h-full object-cover"
            draggable={false}
          />
        </motion.div>
      </AnimatePresence>

      {/* Lighter cinematic gradient stack so the photographs remain visible.
          Left fades to black behind the headline; the right two-thirds of the photo
          shows through almost untouched. */}
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/55 to-transparent pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20 pointer-events-none" />

      {/* Tiny slide indicator — bottom right, brand-quiet */}
      {slides.length > 1 && (
        <div className="absolute bottom-6 right-6 z-20 flex gap-1.5" data-testid="hero-slide-indicators">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Show slide ${i + 1}`}
              className={`h-[2px] transition-all duration-500 ${
                i === index ? 'w-10 bg-white' : 'w-5 bg-white/30 hover:bg-white/60'
              }`}
              data-testid={`hero-slide-indicator-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HeroSlideshow;
